import { useEffect, useState } from 'react';
import type { Page } from '../types';
import { formatBytes, formatMs } from '../utils/format';

// テーブル下部に常時表示される詳細パネル。
// テーブル上で見えない深い情報（h1〜h6 全テキスト・全画像 alt・全リンク・JSON-LD など）を 1 画面で表示する
type DetailTab = 'overview' | 'headings' | 'images' | 'links' | 'jsonld' | 'raw';

const TAB_LABELS: Record<DetailTab, string> = {
  overview: '基本',
  headings: '見出し',
  images: '画像',
  links: 'リンク',
  jsonld: '構造化データ',
  raw: 'JSON',
};

export function PageDetailPanel({ page }: { page: Page | null }) {
  const [tab, setTab] = useState<DetailTab>('overview');

  // ページが切り替わったらタブを基本に戻す
  useEffect(() => {
    if (page) setTab('overview');
  }, [page?.url]);

  if (!page) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 text-xs text-slate-500">
        左のテーブルで行を選ぶと、ここに詳細が表示されます
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white" role="region" aria-label="ページ詳細">
      {/* ヘッダー */}
      <div className="flex shrink-0 items-start gap-3 border-b border-slate-200 px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(page.status, page.error)}`}
            >
              {page.error ?? page.status}
            </span>
            {page.title && (
              <span className="truncate text-sm font-semibold text-slate-900" title={page.title}>
                {page.title}
              </span>
            )}
          </div>
          <a
            href={page.url}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 block break-all font-mono text-xs text-slate-600 hover:text-slate-900 hover:underline"
          >
            {page.url}
          </a>
        </div>
      </div>

      {/* タブ */}
      <div className="flex shrink-0 gap-1 border-b border-slate-200 bg-slate-50 px-3">
        {(Object.keys(TAB_LABELS) as DetailTab[]).map((k) => {
          const warn = tabHasWarning(k, page);
          const warnTitle = warn ? tabWarningSummary(k, page) : undefined;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              title={warnTitle}
              className={`border-b-2 px-3 py-2 text-xs font-medium transition ${
                tab === k
                  ? warn
                    ? 'border-amber-500 text-amber-700'
                    : 'border-blue-600 text-blue-700'
                  : warn
                    ? 'border-transparent text-amber-700 hover:text-amber-800'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {warn && <span className="mr-1" aria-label="警告">⚠</span>}
              {TAB_LABELS[k]}
              {k === 'headings' && headingsTotalCount(page) > 0 && (
                <CountBadge n={headingsTotalCount(page)} warn={warn} />
              )}
              {k === 'images' && (page.images?.length ?? 0) > 0 && (
                <CountBadge n={page.images?.length ?? 0} warn={warn} />
              )}
              {k === 'links' && (page.internalLinks?.length ?? 0) + (page.externalLinks?.length ?? 0) > 0 && (
                <CountBadge n={(page.internalLinks?.length ?? 0) + (page.externalLinks?.length ?? 0)} />
              )}
              {k === 'jsonld' && (page.structuredData?.length ?? 0) > 0 && (
                <CountBadge n={page.structuredData?.length ?? 0} />
              )}
            </button>
          );
        })}
      </div>

      {/* コンテンツ */}
      <div className="flex-1 overflow-y-auto p-5 text-sm">
        {tab === 'overview' && <OverviewTab page={page} />}
        {tab === 'headings' && <HeadingsTab page={page} />}
        {tab === 'images' && <ImagesTab page={page} />}
        {tab === 'links' && <LinksTab page={page} />}
        {tab === 'jsonld' && <JsonLdTab page={page} />}
        {tab === 'raw' && <RawTab page={page} />}
      </div>
    </div>
  );
}

function CountBadge({ n, warn }: { n: number; warn?: boolean }) {
  return (
    <span
      className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${
        warn ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-700'
      }`}
    >
      {n}
    </span>
  );
}

// 各タブに含まれる「要確認」項目があるかを判定。
// TableView の hasWarning と判定基準を揃える（alt欠 / h1異常 / canonical壊 / title欠 / desc欠）
function tabHasWarning(tab: DetailTab, p: Page): boolean {
  switch (tab) {
    case 'overview':
      return !p.title || !p.metaDescription || isCanonicalBroken(p);
    case 'headings':
      return (p.headings?.h1.length ?? 1) !== 1;
    case 'images':
      return (p.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? 0) > 0;
    default:
      return false;
  }
}

function tabWarningSummary(tab: DetailTab, p: Page): string {
  const reasons: string[] = [];
  if (tab === 'overview') {
    if (!p.title) reasons.push('title 欠');
    if (!p.metaDescription) reasons.push('metaDescription 欠');
    if (isCanonicalBroken(p)) reasons.push('canonical 先がエラー');
  } else if (tab === 'headings') {
    const h1n = p.headings?.h1.length ?? 0;
    if (h1n === 0) reasons.push('h1 が無い');
    else if (h1n > 1) reasons.push(`h1 が ${h1n} 個ある`);
  } else if (tab === 'images') {
    const miss = p.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? 0;
    if (miss > 0) reasons.push(`alt 未設定の画像 ${miss} 枚`);
  }
  return reasons.length > 0 ? `要確認: ${reasons.join(' / ')}` : '';
}

function isCanonicalBroken(p: Page): boolean {
  return p.canonicalCheck !== undefined && p.canonicalCheck.status >= 400;
}

function headingsTotalCount(p: Page): number {
  if (!p.headings) return 0;
  return (
    p.headings.h1.length +
    p.headings.h2.length +
    p.headings.h3.length +
    p.headings.h4.length +
    p.headings.h5.length +
    p.headings.h6.length
  );
}

// ========== タブ実装 ==========

function OverviewTab({ page }: { page: Page }) {
  return (
    <div className="space-y-4">
      <Section title="メタ情報">
        <KV label="title" value={page.title} length />
        <KV label="metaDescription" value={page.metaDescription} length />
        <KV label="lang" value={page.lang} />
        <KV label="metaRobots" value={page.metaRobots} />
        <KV label="canonical" value={page.canonical} mono />
        {page.canonicalCheck && (
          <KV
            label="canonical 先のステータス"
            value={`${page.canonicalCheck.status} (${page.canonicalCheck.finalUrl})`}
            mono
            warn={page.canonicalCheck.status >= 400}
          />
        )}
      </Section>

      {page.ogp && (page.ogp.title || page.ogp.description || page.ogp.image || page.ogp.type) && (
        <Section title="OGP">
          <KV label="og:title" value={page.ogp.title} />
          <KV label="og:description" value={page.ogp.description} />
          <KV label="og:image" value={page.ogp.image} mono />
          <KV label="og:type" value={page.ogp.type} />
        </Section>
      )}

      {page.hreflang && page.hreflang.length > 0 && (
        <Section title="hreflang">
          <ul className="space-y-1 text-xs">
            {page.hreflang.map((h, i) => (
              <li key={i} className="flex gap-3 font-mono">
                <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">{h.hreflang}</span>
                <span className="break-all text-slate-600">{h.href}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {page.performance && (
        <Section title="パフォーマンス">
          <KV label="TTFB" value={formatMs(page.performance.ttfb)} />
          <KV label="HTML サイズ" value={formatBytes(page.performance.htmlBytes)} />
          <KV label="JS 描画" value={page.jsRendered ? 'はい (Playwright で取得)' : 'いいえ'} />
        </Section>
      )}

      {page.redirectChain.length > 0 && (
        <Section title={`リダイレクト経路 (${page.redirectChain.length} hop)`}>
          <ol className="space-y-1 text-xs">
            {page.redirectChain.map((r, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="shrink-0 text-slate-400 tabular-nums">{i + 1}.</span>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(r.status)}`}>
                  {r.status}
                </span>
                <span className="break-all font-mono text-slate-700">{r.url}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}
    </div>
  );
}

function HeadingsTab({ page }: { page: Page }) {
  if (!page.headings) {
    return <p className="text-xs text-slate-500">見出し情報がありません</p>;
  }
  const levels: Array<['h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6', string[]]> = [
    ['h1', page.headings.h1],
    ['h2', page.headings.h2],
    ['h3', page.headings.h3],
    ['h4', page.headings.h4],
    ['h5', page.headings.h5],
    ['h6', page.headings.h6],
  ];
  return (
    <div className="space-y-4">
      {levels.map(([level, items]) => (
        <Section
          key={level}
          title={`${level} (${items.length})`}
          warn={level === 'h1' && items.length !== 1}
        >
          {items.length === 0 ? (
            <p className="text-xs text-slate-400">なし</p>
          ) : (
            <ol className="divide-y divide-slate-100 rounded border border-slate-200 bg-white">
              {items.map((t, i) => (
                <li key={i} className="flex items-baseline gap-2 px-2 py-1 text-xs">
                  <span className="shrink-0 w-5 text-right tabular-nums text-slate-400">{i + 1}</span>
                  <span className="flex-1 break-words text-slate-800">
                    {t || <em className="text-slate-400">(空テキスト)</em>}
                  </span>
                  <span className="shrink-0 tabular-nums text-[10px] text-slate-400">{t.length}</span>
                </li>
              ))}
            </ol>
          )}
        </Section>
      ))}
    </div>
  );
}

function ImagesTab({ page }: { page: Page }) {
  const images = page.images;
  if (images === undefined) {
    return <p className="text-xs text-slate-500">画像情報が記録されていません（--no-images / --slim 等）</p>;
  }
  if (images.length === 0) {
    return <p className="text-xs text-slate-500">画像はありません</p>;
  }
  return (
    <ul className="divide-y divide-slate-100 rounded border border-slate-200 bg-white">
      {images.map((img, i) => {
        const missing = img.alt === undefined || img.alt === '';
        return (
          <li
            key={i}
            className={`px-3 py-2 text-xs ${missing ? 'bg-amber-50/50' : ''}`}
          >
            <div className="flex items-baseline gap-2">
              <span title={missing ? 'alt 未指定' : 'alt あり'}>{missing ? '⚠' : '✓'}</span>
              <a
                href={img.src}
                target="_blank"
                rel="noreferrer"
                className="flex-1 break-all font-mono text-slate-700 hover:text-slate-900 hover:underline"
              >
                {img.src}
              </a>
              {img.width && img.height && (
                <span className="shrink-0 tabular-nums text-[10px] text-slate-400">
                  {img.width}×{img.height}
                </span>
              )}
              {img.loading && (
                <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-600">
                  {img.loading}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-slate-600">
              alt:{' '}
              {img.alt === undefined ? (
                <em className="text-slate-400">(未指定)</em>
              ) : img.alt === '' ? (
                <em className="text-slate-400">(空文字 — 装飾画像)</em>
              ) : (
                <span>{img.alt}</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function LinksTab({ page }: { page: Page }) {
  const internal = page.internalLinks;
  const external = page.externalLinks;
  return (
    <div className="space-y-4">
      <Section title={`内部リンク (${internal?.length ?? 0})`}>
        {internal === undefined ? (
          <p className="text-xs text-slate-500">内部リンク詳細が記録されていません（--no-internal-links / --slim 等）</p>
        ) : internal.length === 0 ? (
          <p className="text-xs text-slate-500">なし</p>
        ) : (
          <LinkList items={internal} />
        )}
      </Section>
      <Section title={`外部リンク (${external?.length ?? 0})`}>
        {!external || external.length === 0 ? (
          <p className="text-xs text-slate-500">なし</p>
        ) : (
          <LinkList items={external} />
        )}
      </Section>
    </div>
  );
}

function LinkList({
  items,
}: {
  items: Array<{ url: string; text: string; rel?: string }>;
}) {
  return (
    <ul className="divide-y divide-slate-100 rounded border border-slate-200 bg-white">
      {items.map((l, i) => (
        <li key={i} className="px-3 py-1.5 text-xs">
          <div className="flex items-baseline gap-2">
            <a
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="flex-1 break-all font-mono text-slate-700 hover:text-slate-900 hover:underline"
            >
              {l.url}
            </a>
            {l.rel && (
              <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-600">
                rel={l.rel}
              </span>
            )}
          </div>
          {l.text && <div className="mt-0.5 text-slate-600">アンカー: {l.text}</div>}
        </li>
      ))}
    </ul>
  );
}

function JsonLdTab({ page }: { page: Page }) {
  const sd = page.structuredData;
  if (sd === undefined) {
    return <p className="text-xs text-slate-500">構造化データの記録がありません</p>;
  }
  if (sd.length === 0) {
    return <p className="text-xs text-slate-500">JSON-LD はありません</p>;
  }
  return (
    <div className="space-y-3">
      {sd.map((block, i) => (
        <details
          key={i}
          open={i === 0}
          className="rounded border border-slate-200 bg-white"
        >
          <summary className="cursor-pointer select-none border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
            {`#${i + 1}`}{' '}
            {typeof block === 'object' && block !== null && '@type' in block
              ? `@type=${String((block as Record<string, unknown>)['@type'])}`
              : ''}
          </summary>
          <pre className="overflow-x-auto p-3 font-mono text-[11px] text-slate-700">
            {JSON.stringify(block, null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}

function RawTab({ page }: { page: Page }) {
  return (
    <pre className="overflow-x-auto rounded border border-slate-200 bg-slate-900 p-3 font-mono text-[11px] text-slate-100">
      {JSON.stringify(page, null, 2)}
    </pre>
  );
}

// ========== ヘルパー ==========

function Section({
  title,
  warn,
  children,
}: {
  title: string;
  warn?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3
        className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wide ${
          warn ? 'text-amber-700' : 'text-slate-500'
        }`}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function KV({
  label,
  value,
  mono,
  length,
  warn,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  length?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2 border-b border-slate-100 py-1 text-xs last:border-0">
      <span className="w-32 shrink-0 text-slate-500">{label}</span>
      {value === undefined || value === '' ? (
        <em className="text-slate-400">(未設定)</em>
      ) : (
        <>
          <span
            className={`flex-1 break-all ${mono ? 'font-mono' : ''} ${
              warn ? 'text-amber-700' : 'text-slate-800'
            }`}
          >
            {value}
          </span>
          {length && (
            <span className="shrink-0 tabular-nums text-[10px] text-slate-400">
              {value.length} 字
            </span>
          )}
        </>
      )}
    </div>
  );
}

function statusColor(status: number, error?: string): string {
  if (error) return 'bg-red-100 text-red-700';
  if (status >= 500) return 'bg-red-100 text-red-700';
  if (status >= 400) return 'bg-red-100 text-red-700';
  if (status >= 300) return 'bg-amber-100 text-amber-700';
  if (status >= 200) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
}
